/**
 * ⚠️  SOLO PARA PRUEBAS — página oculta del panel admin.
 *
 * Esta ruta NO aparece en el sidebar ni en ningún menú. Solo es accesible
 * escribiendo `/admin/seed-demo` directamente. El backend bloquea la
 * ejecución cuando el request entra por el host de producción
 * (`api.goflux.io`); ver AdminSeedService.runSeed.
 *
 * Permite sembrar datos de prueba consistentes (1 comercio + 1 usuario
 * Keycloak + adquirente Transbank con credenciales reales del comercio +
 * 6 ventas con hasta 6 cuotas c/u + cuentas de abono ficticias). El form
 * pide:
 *   - businessName + taxId → datos de la empresa (taxId DEBE diferir del tbkRut).
 *   - email + password → la API crea el usuario Keycloak con email validado.
 *   - tbkRut + tbkPassword → la API cifra el password con AES-256-GCM y los
 *     guarda como api_key + encrypted_api_secret en company_acquirers.
 */

import { useState } from 'react';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface RunResult {
  ok: true;
  statementsExecuted: number;
  keycloakId: string;
  taxId: string;
  tbkUsername: string;
}

export default function SeedDemoPage() {
  const [businessName, setBusinessName] = useState('Comercio Demo 2 SpA');
  const [taxId, setTaxId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tbkRut, setTbkRut] = useState('');
  const [tbkPassword, setTbkPassword] = useState('');
  const [siiPassword, setSiiPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    businessName.trim().length > 0 &&
    taxId.trim().length > 0 &&
    email.trim().length > 0 &&
    password.trim().length >= 8 &&
    tbkRut.trim().length > 0 &&
    tbkPassword.trim().length > 0 &&
    siiPassword.trim().length > 0;

  async function handleRun() {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const data = await adminHttpClient.post<RunResult>(getAdminEndpointUrl('seedDemo', 'run'), {
        businessName: businessName.trim(),
        taxId: taxId.trim(),
        email: email.trim(),
        password,
        tbkRut: tbkRut.trim(),
        tbkPassword,
        siiPassword,
      });
      setResult(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AdminLayout title="Seed de demo" subtitle="⚠️ Solo para pruebas — bloqueado en hosts de producción">
      <div className="max-w-2xl space-y-6">
        {/* Aviso prominente */}
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <span className="material-symbols-outlined text-amber-600 text-[20px] mt-0.5">warning</span>
          <div className="space-y-1 text-sm text-amber-900 dark:text-amber-200">
            <p className="font-semibold">Ruta oculta — solo entornos de prueba.</p>
            <p>
              Crea un comercio completo (empresa, usuario Keycloak con email validado, cuenta bancaria, adquirente Transbank, cuentas de abono ficticias scrapeadas, línea
              MEL y 6 ventas con hasta 6 cuotas) ejecutando el SQL empaquetado en el repo del API
              (<code className="mx-1 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 rounded">src/modules/admin/seed/seed-demo-user-2.sql</code>).
              El backend rechaza la operación cuando el request entra por <code>api.goflux.io</code>.
            </p>
          </div>
        </div>

        {/* Sección: Empresa */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 space-y-5">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Empresa</h3>
            <p className="text-xs text-slate-500">
              Datos del comercio. El RUT se usa en <code>companies.tax_id</code>, signers, holder de la cuenta bancaria y rut titular del local A.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="seed-business-name">Razón social</Label>
              <Input
                id="seed-business-name"
                name="seed-business-name"
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Comercio Demo SpA"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="seed-tax-id">RUT empresa</Label>
              <Input
                id="seed-tax-id"
                name="seed-tax-id"
                type="text"
                inputMode="text"
                value={taxId}
                onChange={(e) => setTaxId(e.target.value)}
                placeholder="77.555.444-3"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
            </div>
          </div>
        </div>

        {/* Sección: Acceso del comercio (Keycloak) */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 space-y-5">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Acceso del comercio (Keycloak)</h3>
            <p className="text-xs text-slate-500">
              La API crea el usuario en Keycloak con estos datos y lo deja con el email <strong>validado</strong> para que pueda iniciar sesión de inmediato.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="seed-email">Email</Label>
              <Input
                id="seed-email"
                name="seed-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="demo@example.com"
                autoComplete="email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="seed-password">Password</Label>
              <Input
                id="seed-password"
                name="seed-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                className="font-mono text-xs"
                autoComplete="new-password"
              />
            </div>
          </div>
        </div>

        {/* Sección: Credenciales Transbank */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 space-y-5">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Credenciales Transbank</h3>
            <p className="text-xs text-slate-500">
              Username Transbank (típicamente un RUT) y password en claro. La API cifra el password con AES-256-GCM (
              <code>ENCRYPTION_SECRET_KEY</code>) antes de guardarlo en <code>company_acquirers.encrypted_api_secret</code>.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="seed-tbk-rut">Username Transbank (RUT TBK)</Label>
              {/* autoComplete="off" + name fuera del namespace de email/username
                  para evitar que el navegador ofrezca autofill de cuentas guardadas. */}
              <Input
                id="seed-tbk-rut"
                name="seed-tbk-rut"
                type="text"
                inputMode="text"
                value={tbkRut}
                onChange={(e) => setTbkRut(e.target.value)}
                placeholder="76.999.888-7"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="seed-tbk-pass">Password Transbank</Label>
              {/* autoComplete="new-password" engaña a los password managers para
                  que no rellenen este campo con credenciales guardadas. */}
              <Input
                id="seed-tbk-pass"
                name="seed-tbk-pass"
                type="password"
                value={tbkPassword}
                onChange={(e) => setTbkPassword(e.target.value)}
                placeholder="Password en claro"
                className="font-mono text-xs"
                autoComplete="new-password"
              />
            </div>
          </div>
        </div>

        {/* Sección: Credenciales SII */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 space-y-5">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Credenciales SII</h3>
            <p className="text-xs text-slate-500">
              El username SII es el RUT de la empresa (campo <strong>RUT empresa</strong> de arriba). Solo necesitamos el password en claro: la API lo cifra con
              AES-256-GCM y lo guarda en <code>company_sii_credentials.encrypted_password</code>.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="seed-sii-pass">Password SII</Label>
              <Input
                id="seed-sii-pass"
                name="seed-sii-pass"
                type="password"
                value={siiPassword}
                onChange={(e) => setSiiPassword(e.target.value)}
                placeholder="Password en claro"
                className="font-mono text-xs"
                autoComplete="new-password"
              />
            </div>
          </div>
        </div>

        {/* Resultado (arriba del botón para que sea visible sin scrollear) */}
        {result && (
          <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4 flex items-start gap-3">
            <span className="material-symbols-outlined text-emerald-600 text-[20px] mt-0.5">check_circle</span>
            <div className="text-sm text-emerald-900 dark:text-emerald-200 space-y-1">
              <p className="font-semibold">Seed ejecutado.</p>
              <ul className="text-xs space-y-0.5">
                <li>
                  Statements aplicados: <strong>{result.statementsExecuted}</strong>
                </li>
                <li>
                  RUT empresa: <code className="font-mono">{result.taxId}</code>
                </li>
                <li>
                  Username TBK: <code className="font-mono">{result.tbkUsername}</code>
                </li>
                <li>
                  Keycloak ID: <code className="font-mono break-all">{result.keycloakId}</code>
                </li>
              </ul>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 flex items-start gap-3">
            <span className="material-symbols-outlined text-red-600 text-[20px] mt-0.5">error</span>
            <div className="text-sm text-red-900 dark:text-red-200">
              <p className="font-semibold">No se pudo ejecutar el seed.</p>
              <p className="font-mono text-xs mt-1 break-all">{error}</p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <Button onClick={handleRun} disabled={!canSubmit || loading} className="gap-1.5 bg-blue-600 hover:bg-blue-500">
            {loading ? (
              <>
                <span className="material-symbols-outlined text-[16px] animate-spin">progress_activity</span>
                Ejecutando…
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[16px]">play_arrow</span>
                Ejecutar seed
              </>
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
